import { ensureViewrPathStructure } from "../../utils/filePathUtils";

const VDB_GCS_PATH_SESSION_STORAGE_KEY = "viewr:vdbGcsPathByViewr";

const vdbGcsPathByViewrPath = new Map<string, string>();
const isStorageUri = (value: string | null | undefined): boolean =>
  /^(gs|s3):\/\/[^/]+\/.+/.test((value ?? "").trim());

const normalizeViewrMapKey = (viewrGcsPath: string | null | undefined): string => {
  const trimmed = (viewrGcsPath ?? "").trim();
  if (!trimmed) {
    return "";
  }

  return ensureViewrPathStructure(trimmed);
};

const readSessionStorageMap = (): Record<string, string> => {
  if (typeof window === "undefined" || !window.sessionStorage) {
    return {};
  }

  try {
    const raw = window.sessionStorage.getItem(VDB_GCS_PATH_SESSION_STORAGE_KEY);
    if (!raw) {
      return {};
    }

    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return {};
    }

    const result: Record<string, string> = {};
    for (const [key, value] of Object.entries(parsed)) {
      if (typeof key !== "string" || typeof value !== "string") {
        continue;
      }

      const normalizedKey = normalizeViewrMapKey(key);
      const normalizedVdb = value.trim();
      if (!normalizedKey || !isStorageUri(normalizedVdb)) {
        continue;
      }

      result[normalizedKey] = normalizedVdb;
    }

    return result;
  } catch {
    return {};
  }
};

const writeSessionStorageMap = (map: ReadonlyMap<string, string>): void => {
  if (typeof window === "undefined" || !window.sessionStorage) {
    return;
  }

  try {
    const payload: Record<string, string> = {};
    map.forEach((vdbPath, viewrKey) => {
      payload[viewrKey] = vdbPath;
    });
    window.sessionStorage.setItem(
      VDB_GCS_PATH_SESSION_STORAGE_KEY,
      JSON.stringify(payload),
    );
  } catch {
    // Ignore quota / private-mode failures; in-memory map still works this session.
  }
};

const hydrateMemoryFromSessionStorage = (viewrKey: string): string => {
  const stored = readSessionStorageMap();
  const vdbPath = stored[viewrKey];
  if (!isStorageUri(vdbPath)) {
    return "";
  }

  vdbGcsPathByViewrPath.set(viewrKey, vdbPath);
  return vdbPath;
};

export const rememberViewrVdbGcsPath = (
  viewrGcsPath: string | null | undefined,
  vdbGcsPath: string | null | undefined,
): void => {
  const viewrKey = normalizeViewrMapKey(viewrGcsPath);
  const normalizedVdb = (vdbGcsPath ?? "").trim();
  if (!viewrKey || !isStorageUri(normalizedVdb)) {
    return;
  }

  vdbGcsPathByViewrPath.set(viewrKey, normalizedVdb);
  writeSessionStorageMap(vdbGcsPathByViewrPath);
};

export const resolveViewrVdbGcsPath = (
  viewrGcsPath: string | null | undefined,
): string => {
  const viewrKey = normalizeViewrMapKey(viewrGcsPath);
  if (!viewrKey) {
    return "";
  }

  const inMemory = vdbGcsPathByViewrPath.get(viewrKey);
  if (isStorageUri(inMemory)) {
    return inMemory;
  }

  return hydrateMemoryFromSessionStorage(viewrKey);
};

/** Home/세션 teardown: in-memory만 비움. sessionStorage는 동일 탭 재진입용으로 유지. */
export const clearViewrVdbGcsPathMemory = (): void => {
  vdbGcsPathByViewrPath.clear();
};

export const clearViewrVdbGcsPathSession = (): void => {
  vdbGcsPathByViewrPath.clear();
  if (typeof window !== "undefined" && window.sessionStorage) {
    try {
      window.sessionStorage.removeItem(VDB_GCS_PATH_SESSION_STORAGE_KEY);
    } catch {
      // ignore
    }
  }
};

/** @internal test-only */
export const __viewrVdbGcsPathSessionTest = {
  size: () => vdbGcsPathByViewrPath.size,
  sessionStorageKey: VDB_GCS_PATH_SESSION_STORAGE_KEY,
};
